import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCourses, getRecommended, getTrending } from '../../api/marketplace';

// ──────────────────────────────────────────────
// Async Thunks
// ──────────────────────────────────────────────

export const fetchCourses = createAsyncThunk(
  'marketplace/fetchCourses',
  async (_, { rejectWithValue }) => {
    try {
      return await getCourses();
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch courses' });
    }
  }
);

export const fetchRecommended = createAsyncThunk(
  'marketplace/fetchRecommended',
  async (_, { rejectWithValue }) => {
    try {
      return await getRecommended();
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch recommended courses' });
    }
  }
);

export const fetchTrending = createAsyncThunk(
  'marketplace/fetchTrending',
  async (_, { rejectWithValue }) => {
    try {
      return await getTrending();
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch trending courses' });
    }
  }
);

// ──────────────────────────────────────────────
// Slice
// ──────────────────────────────────────────────

const marketplaceSlice = createSlice({
  name: 'marketplace',
  initialState: {
    courses: [],
    recommended: [],
    trending: [],
    loading: {
      courses: false,
      recommended: false,
      trending: false,
    },
    error: {
      courses: null,
      recommended: null,
      trending: null,
    },
  },
  reducers: {
    clearMarketplaceErrors: (state) => {
      state.error = { courses: null, recommended: null, trending: null };
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchCourses ──
      .addCase(fetchCourses.pending, (state) => {
        state.loading.courses = true;
        state.error.courses = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.loading.courses = false;
        state.courses = action.payload.data || [];
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.loading.courses = false;
        state.error.courses = action.payload?.message || 'Failed to fetch courses';
      })
      // ── fetchRecommended ──
      .addCase(fetchRecommended.pending, (state) => {
        state.loading.recommended = true;
        state.error.recommended = null;
      })
      .addCase(fetchRecommended.fulfilled, (state, action) => {
        state.loading.recommended = false;
        state.recommended = action.payload.data || [];
      })
      .addCase(fetchRecommended.rejected, (state, action) => {
        state.loading.recommended = false;
        state.error.recommended = action.payload?.message || 'Failed to fetch recommended courses';
      })
      // ── fetchTrending ──
      .addCase(fetchTrending.pending, (state) => {
        state.loading.trending = true;
        state.error.trending = null;
      })
      .addCase(fetchTrending.fulfilled, (state, action) => {
        state.loading.trending = false;
        state.trending = action.payload.data || [];
      })
      .addCase(fetchTrending.rejected, (state, action) => {
        state.loading.trending = false;
        state.error.trending = action.payload?.message || 'Failed to fetch trending courses';
      });
  },
});

export const { clearMarketplaceErrors } = marketplaceSlice.actions;

export default marketplaceSlice.reducer;
